/**
 * -----------------------------------------------------------------------------
 * Geozone Category Edit Form
 * -----------------------------------------------------------------------------
 * These components are used to render the form for editing the geozone category details.
 */

import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  useOrganizationGeozonesQuery,
  useOrganizationUsersQuery,
} from "../../../api/network/adminApiServices";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  PseudoSelect,
} from "../../../components/admin/formFields";
import { APP_CONFIG } from "../../../constants/constants";
import { useLoggedInUserData } from "../../../utils/user";

interface GCGeneralDetailFormProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
  showUserAcl?: boolean;
  setShowUserAcl?: (value: boolean) => void;
}

export const GCGeneralDetailForm: FC<GCGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
  showUserAcl,
  setShowUserAcl,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories.detailsPage.form",
  });
  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldInput
        label="Geozone Category ID"
        type="text"
        id="categoryid"
        name="categoryid"
        value={values.categoryid}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.categoryid}
        error={errors.categoryid}
        disabled={!userCanEdit}
        readOnly={true}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label="Display Name"
        type="text"
        id="displayname"
        name="displayname"
        value={values.displayname}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.displayname}
        error={errors.displayname}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldInput
        label="Description"
        type="text"
        id="description"
        name="description"
        value={values.description}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched.description}
        error={errors.description}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <div className="col-span-12">
        <AdminFormFieldInput
          label="Notes"
          type="text"
          id="notes"
          name="notes"
          value={values.notes}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched.notes}
          error={errors.notes}
          disabled={!userCanEdit}
          detailsFormField={true}
        />
      </div>
    </div>
  );
};

export const GCGeozonesForm: FC<GCGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories.detailsPage",
  });
  const { t: tMain } = useTranslation();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { data: dataOrgGeozones, isFetching: isFetchingOrgGeozones } =
    useOrganizationGeozonesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE * 10,
      search: "",
    });

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {loadingData || isFetchingOrgGeozones ? (
        <PseudoSelect label={"Geozones"} />
      ) : (
        <>
          <AdminFormFieldDropdown
            loadingData={loadingData}
            dimensionClass="w-full min-h-11"
            label={"Geozones"}
            id="geozones"
            name="geozones"
            value={values.geozones}
            options={(dataOrgGeozones?.results || []).map((item) => ({
              label: item.geozoneid,
              value: item.geozoneid,
            }))}
            onChange={(e) => {
              formikSetValue("geozones", e);
            }}
            onBlur={(e) => {
              formikSetTouched("geozones", true);
              handleBlur(e);
            }}
            touched={touched.geozones}
            error={errors.geozones}
            disabled={!userCanEdit}
            detailsFormField={true}
            isMulti={true}
            menuPlacement="top"
          />
        </>
      )}
    </div>
  );
};

export const GCUsersForm: FC<GCGeneralDetailFormProps> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.geozone_categories.detailsPage",
  });
  const { t: tMain } = useTranslation();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const { data: dataOrgUsers, isFetching: isFetchingOrgUsers } =
    useOrganizationUsersQuery({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE * 10,
      search: "",
    });

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      {loadingData || isFetchingOrgUsers ? (
        <PseudoSelect label={"Users"} />
      ) : (
        <>
          <AdminFormFieldDropdown
            loadingData={loadingData}
            dimensionClass="w-full min-h-11"
            label={"Users"}
            id="users"
            name="users"
            value={values.users}
            options={(dataOrgUsers?.results || []).map((item) => ({
              label: item.userid,
              value: item.userid,
            }))}
            onChange={(e) => {
              formikSetValue("users", e);
            }}
            onBlur={(e) => {
              formikSetTouched("users", true);
              handleBlur(e);
            }}
            touched={touched.users}
            error={errors.users}
            disabled={!userCanEdit}
            detailsFormField={true}
            isMulti={true}
            menuPlacement="top"
          />
        </>
      )}
    </div>
  );
};
